import { useEffect } from "react";

import { AttributeType } from "@energy-advice/easas-api";
import { ProductTemplateEntryType, useCreateProductTemplateEntries } from "api/api-hooks/product-templates-hooks";
import { IProductTemplate, ITableDataEntryModified, ITableDataEntryValue, MultiValue } from "components/dashboard/product-template-table/utils/interfaces";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import useCsvReader, { ParseResultData } from "hooks/useCsvReader";
import { T } from "services/lang/LanguageService";
import { CellSubType } from "utils/enums";
import { TimeSpan, convertStringToTimeSpan } from "utils/time-utils";

const useProductTemplateImportEntries = (configData: IProductTemplate | undefined, params: ProductTemplateEntryType) => {
  const {handleFileChange, data: csvData} = useCsvReader();

  const { mutate } = useCreateProductTemplateEntries(params, {
    onSuccess: () => {
      easasAlert({ summary: T("alert.success"), detail: T("alert.success"), severity: "success" });
    },
    onFailure: error => {
      easasAlert({ summary: T("alert.error"), detail: error.message, severity: "error" });
    },
  });

  useEffect(() => {
    if (csvData.length > 0 && configData?.data?.attributes && configData?.data?.attributes.length > 0) {
      const entriesModified: ITableDataEntryModified[] = csvData.map(row => {
        const entries = configData.data!.attributes.reduce(
          (array: ITableDataEntryValue[], attr) => {
            if (
              attr.type === AttributeType.USER_SIGNATURE ||
              attr.type === AttributeType.FOREIGN_KEY ||
              attr.type === AttributeType.MULTI_FOREIGN_KEY ||
              attr.locked
            ) {
              return array;
            }
            let value = row[attr.displayName as keyof ParseResultData] as
              | number
              | string
              | TimeSpan
              | MultiValue;

            if (value) {
              let values: (string | number | TimeSpan)[] = [];

              switch (attr.type) {
                case AttributeType.TIMESPAN:
                  value = convertStringToTimeSpan(value as string);

                  array.push({
                    referenceName: attr.referenceName,
                    type: attr.type,
                    value,
                  });
                  break;
                case AttributeType.INSTANT:
                case AttributeType.DATETIME:
                  array.push({
                    referenceName: attr.referenceName,
                    type: attr.type,
                    value: new Date(value as string).toISOString(),
                  });
                  break;
                case AttributeType.MULTI_VALUE:
                  switch (attr.subType) {
                    case CellSubType.TIMESPAN:
                      values = (value as string).split(" | ").map(v => convertStringToTimeSpan(v));
                      break;
                    case CellSubType.DOUBLE:
                    case CellSubType.LONG:
                      values = (value as string).split(" | ").map(v => +v);
                      break;
                    default:
                      values = (value as string).split(" | ");
                      break;
                  }

                  value = {
                    subType: attr.subType!,
                    values,
                  };

                  array.push({
                    referenceName: attr.referenceName,
                    type: attr.type,
                    value,
                  });
                  break;
                default:
                  array.push({
                    referenceName: attr.referenceName,
                    type: attr.type,
                    value,
                  });
                  break;
              }
            }
            return array;
          },
          []
        );
        return {templateRef: configData.safeName, entryData: entries};
      });

      if (configData?.safeName && configData.data && entriesModified.length > 0) {
        mutate(entriesModified);
      }
    }
  }, [configData?.data, configData?.safeName, csvData, mutate]);

  return { handleFileChange } as const;
}

export default useProductTemplateImportEntries;